<template lang="pug">
.ui-timepicker
  .ui-timepicker-input(
    :class="inputClasses"
    ref="reference"
    @click="handleShow()"
  )
    .ui-timepicker-input__icon
      UiIcon(name="Time")
    .ui-timepicker-input__label {{ valueLabel }}
  .ui-timepicker-dropdown(
    v-if="opened"
    ref="floating"
    :style="floatingStyles"
  )
    .ui-timepicker-dropdown__container
      UiTimepickerSpin(
        v-model="hours"
        @update:modelValue="handleEmitModelValue()"
        :max="23"
      )
      .ui-timepicker-dropdown__time-divider :
      UiTimepickerSpin(
        v-model="minutes"
        @update:modelValue="handleEmitModelValue()"
        :max="59"
      )
</template>

<script>
import { defineComponent, ref } from "vue";
import { useFloating, offset } from "@floating-ui/vue";
import UiTimepickerSpin from "./UiTimepickerSpin.vue";

export default defineComponent({
  emits: ["update:modelValue"],

  components: {
    UiTimepickerSpin,
  },

  props: {
    modelValue: {
      type: String,
      default: null,
    },
  },

  setup() {
    const reference = ref(null);
    const floating = ref(null);
    const { floatingStyles } = useFloating(reference, floating, {
      placement: "bottom-start",
      middleware: [offset(4)],
    });

    return {
      reference,
      floating,
      floatingStyles,
    };
  },

  data() {
    return {
      hours: 0,
      minutes: 0,
      opened: false,
    };
  },

  computed: {
    inputClasses() {
      return {
        "ui-timepicker-input--focused": this.opened,
      };
    },

    valueLabel() {
      return [this.hours, this.minutes]
        .map((value) =>
          value !== null ? String(value).padStart(2, "0") : "--",
        )
        .join(":");
    },
  },

  watch: {
    modelValue() {
      this.syncModelValue();
    },
  },

  methods: {
    syncModelValue() {
      try {
        const [rawHours, rawMinutes] = this.modelValue.split(":");
        const hours = parseInt(rawHours);
        const minutes = parseInt(rawMinutes);
        this.hours = hours;
        this.minutes = minutes;
      } catch {
        this.hours = 0;
        this.minutes = 0;
      }
    },

    handleEmitModelValue() {
      this.$emit("update:modelValue", this.valueLabel);
    },

    handleShow() {
      this.opened = true;
      this.$emit("show");
    },

    handleHide() {
      this.opened = false;
      this.$emit("hide");
    },

    clickAway(event) {
      const path = event.composedPath();
      const isOutClick =
        !path.includes(this.$refs.reference) &&
        !path.includes(this.$refs.floating);

      if (!isOutClick) {
        return;
      }

      this.$emit("outClick", event);
      this.handleHide();
    },

    startSelect() {
      this.open = true;
    },
  },

  mounted() {
    window.addEventListener("mousedown", this.clickAway);
  },

  beforeDestroy() {
    window.removeEventListener("mousedown", this.clickAway);
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheet/_variables.scss";
@import "@/assets/stylesheet/_form.scss";

.ui-timepicker-input {
  display: flex;
  width: 100%;
  height: calc(1.5em + 1.1rem);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  background-color: #fff;
  background-clip: padding-box;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  border-radius: 21px;
  border: 1px solid var(--bb-input-border);
  align-items: center;
  user-select: none;
  cursor: pointer;
  padding: 0 0.75rem;
  gap: 0.75rem;

  &--focused {
    border-color: var(--bb-primary);
  }

  &__icon {
    font-size: 1.2rem;
    color: var(--bb-gray-700);
  }
}

.ui-timepicker-dropdown {
  z-index: 1100;
  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  border: 1px solid #f1f3f7;
  background-color: #fff;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  box-shadow:
    0 1px 3px rgba(47, 54, 59, 0.08),
    0 5px 10px rgba(47, 54, 59, 0.04);
  border-radius: 10px;

  &__container {
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__time-divider {
    font-size: 1.5rem;
  }
}
</style>
